// apps/web/src/routes/settings-working-hours.tsx
import { useState, useEffect } from 'react'
import useSWR from 'swr'
import { Button } from '@viecxanh/chat-ui/button'
import { Switch } from '@viecxanh/chat-ui/switch'
import { Input } from '@viecxanh/chat-ui/input'
import { Skeleton } from '@viecxanh/chat-ui/skeleton'
import { api } from '../lib/api-client'
import type { WorkingHoursDay } from '@viecxanh/chat-shared'

const DAYS = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7']

export function WorkingHoursPage() {
  const { data, isLoading, mutate } = useSWR<WorkingHoursDay[]>(
    '/api/admin/settings/working-hours',
    (url: string) => api.get<WorkingHoursDay[]>(url),
  )

  const [hours, setHours] = useState<WorkingHoursDay[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (data) setHours(data)
  }, [data])

  const update = (dayOfWeek: number, patch: Partial<WorkingHoursDay>) => {
    setHours((prev) => prev.map((h) => (h.day_of_week === dayOfWeek ? { ...h, ...patch } : h)))
  }

  const handleSave = async () => {
    for (const h of hours) {
      if (h.is_active && h.start_time >= h.end_time) {
        setError(`Giờ bắt đầu phải trước giờ kết thúc (${DAYS[h.day_of_week]})`)
        return
      }
    }
    setSaving(true)
    setError('')
    try {
      await api.put('/api/admin/settings/working-hours', { days: hours })
      await mutate()
    } catch {
      setError('Lưu thất bại, thử lại.')
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) return <Skeleton className="h-64 w-full" />

  return (
    <div className="space-y-4 max-w-lg">
      <h2 className="text-lg font-semibold">Giờ làm việc</h2>
      <p className="text-sm text-slate-500">Múi giờ: Asia/Ho_Chi_Minh (GMT+7)</p>

      <div className="space-y-2">
        {hours.map((h) => (
          <div key={h.day_of_week} className="flex items-center gap-3 py-2 border-b">
            <Switch checked={h.is_active} onCheckedChange={(v) => update(h.day_of_week, { is_active: v })} />
            <span className="w-20 text-sm font-medium">{DAYS[h.day_of_week]}</span>
            <Input
              type="time"
              value={h.start_time}
              disabled={!h.is_active}
              onChange={(e) => update(h.day_of_week, { start_time: e.target.value })}
              className="h-8 w-28 disabled:opacity-50"
            />
            <span className="text-slate-400">—</span>
            <Input
              type="time"
              value={h.end_time}
              disabled={!h.is_active}
              onChange={(e) => update(h.day_of_week, { end_time: e.target.value })}
              className="h-8 w-28 disabled:opacity-50"
            />
          </div>
        ))}
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}
      <Button onClick={() => void handleSave()} disabled={saving} className="bg-brand hover:bg-brand-dark">
        {saving ? 'Đang lưu...' : 'Lưu thay đổi'}
      </Button>
    </div>
  )
}
